import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui';
import {
  vendorRejectSchema,
  type VendorRejectValues,
} from '../schemas/vendorReviewSchema';

export interface VendorRejectModalProps {
  isOpen: boolean;
  applicationName: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (values: VendorRejectValues) => void;
  onCancel: () => void;
}

export const VendorRejectModal = ({
  isOpen,
  applicationName,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: VendorRejectModalProps) => {
  const { t } = useTranslation('vendors');
  const form = useForm<VendorRejectValues>({
    resolver: zodResolver(vendorRejectSchema),
    defaultValues: { reason: '' },
    mode: 'onBlur',
  });
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.reset({ reason: '' });
      requestAnimationFrame(() => reasonRef.current?.focus());
    }
  }, [isOpen, form]);

  const { ref: registerRef, ...reasonRegister } = form.register('reason');
  const error = form.formState.errors.reason?.message;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="vendor-reject-modal" className="max-w-md">
        <DialogTitle>{t('vendors.actions.reject')}</DialogTitle>
        <DialogDescription>
          {t('vendors.actionModal.subject', { vendorName: applicationName })}
        </DialogDescription>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="vendor-reject-reason"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('vendors.actionModal.rejectReason')}
              <span aria-hidden="true" className="ml-1 text-[color:var(--signal-danger)]">
                *
              </span>
            </label>
            <textarea
              id="vendor-reject-reason"
              {...reasonRegister}
              ref={(el) => {
                registerRef(el);
                reasonRef.current = el;
              }}
              required
              aria-required="true"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'vendor-reject-reason-error' : undefined}
              maxLength={1000}
              rows={4}
              className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] placeholder:text-[color:var(--content-muted)] focus-visible:outline-none focus-visible:ring-2"
              style={{ borderColor: 'var(--content-muted)' }}
            />
            {error ? (
              <span
                id="vendor-reject-reason-error"
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(error, { defaultValue: error })}
              </span>
            ) : null}
          </div>

          {errorMessage ? (
            <p
              role="alert"
              aria-live="polite"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('vendors.actions.cancel')}
            </Button>
            <Button type="submit" variant="danger" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('vendors.actionModal.submitting')
                : t('vendors.actions.rejectConfirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
